/**
 * UserDashboard Page
 * 
 * Main dashboard page for viewing user information.
 * Layout:
 * - UserSearch
 * - UserStatusCard | PointsSummary (3 cards)
 * - UserOffers (with assign button)
 * - UserRedemptions | UserTransactions
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserSearch } from '@/components/users/UserSearch';
import { UserStatusCard } from '@/components/users/UserStatusCard';
import { PointsSummary } from '@/components/users/PointsSummary';
import { UserOffers } from '@/components/users/UserOffers';
import { UserRedemptions } from '@/components/users/UserRedemptions';
import { UserTransactions } from '@/components/users/UserTransactions';
import { useUserDashboard } from '@/app/hooks/useUserDashboard';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import { auditService } from '@/app/services';
import { logger } from '@/app/utils/logger';
import './UserDashboard.scss';

export const UserDashboard: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { setActions } = useHeaderActions();
  
  const [searchUserId, setSearchUserId] = useState<number | null>(
    id ? parseInt(id, 10) : null
  );

  const { data, isLoading, error, refresh } = useUserDashboard(searchUserId);

  // Set header actions when user is selected
  useEffect(() => {
    if (searchUserId && data) {
      setActions(
        <button className="btn-refresh" onClick={refresh}>
          ↻ Refresh
        </button>
      );
    } else {
      setActions(null);
    }
    return () => setActions(null);
  }, [setActions, searchUserId, data, refresh]);

  // Log record view when user data is loaded
  useEffect(() => {
    if (searchUserId && data && data.status) {
      auditService.logRecordView('user', String(searchUserId), {
        page_url: `/users/${searchUserId}`,
      }).catch((err) => logger.warn('Failed to log record view', { error: err }));
    }
  }, [searchUserId, data]);

  const handleSearch = useCallback((userId: number) => {
    setSearchUserId(userId);
    navigate(`/users/${userId}`, { replace: true });
  }, [navigate]);

  const handleAssignSuccess = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleRemoveOffer = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleCreateAdjustment = useCallback(() => {
    refresh();
  }, [refresh]);

  // No user selected - show search only
  if (!searchUserId) {
    return (
      <div className="user-dashboard">
        <UserSearch onSearch={handleSearch} />
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="user-dashboard">
        <UserSearch onSearch={handleSearch} initialValue={searchUserId} />
        <div className="user-dashboard-loading">
          <div className="spinner" />
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="user-dashboard">
        <UserSearch onSearch={handleSearch} initialValue={searchUserId} />
        <div className="user-dashboard-error">
          <p>⚠️ {error}</p>
          <button onClick={refresh}>Retry</button>
        </div>
      </div>
    );
  }

  // User not found
  if (!data || !data.status) {
    return (
      <div className="user-dashboard">
        <UserSearch onSearch={handleSearch} initialValue={searchUserId} />
        <div className="user-dashboard-not-found">
          <p>User not found</p>
          <p className="user-dashboard-not-found-hint">
            User ID: {searchUserId}
          </p>
        </div>
      </div>
    );
  }

  // User found - full dashboard
  return (
    <div className="user-dashboard">
      <UserSearch onSearch={handleSearch} initialValue={searchUserId} />

      <div className="user-dashboard-grid">
        <div className="user-dashboard-main">
          <UserStatusCard status={data.status} />
        </div>
        <div className="user-dashboard-sidebar">
          <PointsSummary
            availableBalance={data.status.total_points}
            earnedToday={data.pointsToday}
            lifetimeEarned={data.lifetimeEarned}
          />
        </div>
      </div>

      <UserOffers
        offers={data.activeOffers}
        rewardLimits={data.rewardLimits}
        userId={searchUserId}
        partnerProductId={data.status.partner_product_id}
        onAssign={handleAssignSuccess}
        onRemove={handleRemoveOffer}
      />

      <div className="user-dashboard-grid">
        <div className="user-dashboard-main">
          <UserRedemptions fulfillments={data.fulfillments} userId={searchUserId} />
        </div>
        <div className="user-dashboard-sidebar">
          <UserTransactions
            transactions={data.recentTransactions}
            userId={searchUserId}
            partnerProductId={data.status.partner_product_id}
            onCreateAdjustment={handleCreateAdjustment}
          />
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;



